import { useMemo, useState } from 'react'
import type { JSX } from 'react'
import { formatTime } from '../lib/format'
import { Modal } from './Modal'

/**
 * Подтверждение объединения соседних реплик (docs/SEGMENTATION.md,
 * «Объединение и создание реплик»). Один диалог на два места — редактор
 * и черновик разбора: вопрос там один и тот же, а разница только в том,
 * есть ли чему пропасть.
 */

export type MergeCandidate = {
  /** `Line.id` в редакторе, `DraftLine.key` в черновике. */
  id: string
  start: number
  end: number
  /** Роль или говорящий; '' — не назначена. */
  roleId: string
  roleName: string
  roleColor: string
  text: string
  /** Сколько дублей записано у этой реплики. В черновике всегда 0. */
  takes: number
}

export type MergeRoleOption = { id: string; name: string; color: string }

export type MergeLinesDialogProps = {
  /** Строго в порядке следования: первая задаёт `start`, последняя — `end`. */
  candidates: readonly MergeCandidate[]
  roleOptions: readonly MergeRoleOption[]
  /**
   * true — объединение уходит через `PUT /api/projects/{id}/lines`, который
   * сохраняет дубли только у реплик с вернувшимися `id`. Значит записи всех,
   * кроме первой, пропадут — и сказать об этом надо до операции.
   */
  takesAtRisk: boolean
  busy?: boolean
  onConfirm: (roleId: string) => void
  onCancel: () => void
}

const NO_ROLE_COLOR = '#5a6577'

function pluralLines(count: number): string {
  const mod10 = count % 10
  const mod100 = count % 100
  if (mod10 === 1 && mod100 !== 11) return 'реплику'
  return 'реплики'
}

export function MergeLinesDialog({
  candidates,
  roleOptions,
  takesAtRisk,
  busy = false,
  onConfirm,
  onCancel,
}: MergeLinesDialogProps): JSX.Element {
  const first = candidates[0]
  const last = candidates[candidates.length - 1]
  const [roleId, setRoleId] = useState<string>(first?.roleId ?? '')

  // Роли разные — молча взять первую нельзя: половина реплики уедет к чужому
  // персонажу, и заметится это только в игре.
  const roleChoices = useMemo(() => {
    const seen = new Set<string>()
    const result: MergeRoleOption[] = []
    for (const candidate of candidates) {
      if (seen.has(candidate.roleId)) continue
      seen.add(candidate.roleId)
      const known = roleOptions.find((role) => role.id === candidate.roleId)
      result.push({
        id: candidate.roleId,
        name: known?.name ?? candidate.roleName,
        color: known?.color ?? candidate.roleColor,
      })
    }
    return result
  }, [candidates, roleOptions])

  const mixedRoles = roleChoices.length > 1
  const lostTakes = takesAtRisk
    ? candidates.slice(1).reduce((sum, candidate) => sum + candidate.takes, 0)
    : 0
  const keptTakes = takesAtRisk ? (first?.takes ?? 0) : 0

  const mergedText = candidates
    .map((candidate) => candidate.text.trim())
    .filter((part) => part.length > 0)
    .join(' ')

  return (
    <Modal
      title={`Объединить ${candidates.length} ${pluralLines(candidates.length)}`}
      onClose={onCancel}
      footer={
        <>
          <button type="button" className="ghost" onClick={onCancel} disabled={busy}>
            Отмена
          </button>
          <span className="spacer" />
          <button
            type="button"
            className={lostTakes > 0 ? 'danger' : 'primary'}
            disabled={busy || candidates.length < 2}
            onClick={() => onConfirm(roleId)}
          >
            {busy ? 'Объединяю…' : lostTakes > 0 ? 'Объединить и потерять дубли' : 'Объединить'}
          </button>
        </>
      }
    >
      <div className="merge-list">
        {candidates.map((candidate, index) => (
          <div key={candidate.id} className="merge-row">
            <span className="faint mono">{index + 1}</span>
            <span
              className="role-swatch"
              style={{ background: candidate.roleColor || NO_ROLE_COLOR }}
            />
            <span className="mono faint">
              {formatTime(candidate.start)} → {formatTime(candidate.end)}
            </span>
            <span className="merge-text">{candidate.text.trim() || '— без текста —'}</span>
            {candidate.takes > 0 ? (
              <span className="badge" title="Записанных дублей">
                ● {candidate.takes}
              </span>
            ) : null}
          </div>
        ))}
      </div>

      {first && last ? (
        <p className="faint" style={{ margin: 0 }}>
          Получится одна реплика{' '}
          <span className="mono">
            {formatTime(first.start)} → {formatTime(last.end)}
          </span>
          {mergedText ? ` с текстом «${mergedText}»` : ' без текста'}.
        </p>
      ) : null}

      {mixedRoles ? (
        <fieldset className="analysis-fieldset">
          <legend>Роли разные — чью оставить?</legend>
          {roleChoices.map((role) => (
            <label key={role.id || 'none'} className="row check">
              <input
                type="radio"
                name="merge-role"
                checked={roleId === role.id}
                disabled={busy}
                onChange={() => setRoleId(role.id)}
              />
              <span className="role-swatch" style={{ background: role.color || NO_ROLE_COLOR }} />
              <span>{role.id ? role.name : 'без роли'}</span>
            </label>
          ))}
        </fieldset>
      ) : null}

      {lostTakes > 0 ? (
        <div className="notice warn">
          <strong>
            Пропадут записанные дубли: {lostTakes}
          </strong>
          <span className="faint">
            Объединение заменяет разметку целиком: сохранятся только дубли первой реплики
            {keptTakes > 0 ? ` (их ${keptTakes})` : ''}, записи остальных удалятся вместе с их
            репликами. Отката нет — если дубли нужны, сведите проект до объединения.
          </span>
        </div>
      ) : takesAtRisk ? (
        <p className="faint" style={{ margin: 0 }}>
          Дублей у объединяемых реплик нет — терять нечего.
        </p>
      ) : null}
    </Modal>
  )
}
